import {
  Box,
  Typography,
  Paper,
  Chip,
  Divider,
  CircularProgress,
  IconButton,
  Link,
  Tooltip,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  OpenInNew as OpenInNewIcon,
  AttachMoney as MoneyIcon,
} from '@mui/icons-material';
import Layout from '../components/Layout';
import { useEffect, useState } from 'react';
import api from '../api/axios';
import { useNavigate, useParams } from 'react-router-dom';
import { cleanBodyText } from '../utils/pressReleaseText';

// Types
interface PressRelease {
  id: string;
  title: string;
  url: string | null;
  publish_date: string | null;
  body_text: string | null;
  fraud_type: string;
  posture: string;
  is_qui_tam: boolean;
}

interface RelatedRelease {
  id: string;
  release_id: string;
  release_type: string;
  release_stage: string | null;
  title: string;
  url: string | null;
  publish_date: string | null;
  body_text: string | null;
}

interface PressReleaseDetailData {
  id: string;
  case_name: string;
  case_number: string | null;
  fraud_type: string;
  healthcare_subtype: string | null;
  settlement_usd: string | null;
  extracted_settlement_usd: string | null;
  settlement_evidence: string | null;
  alleged_loss_usd: string | null;
  alleged_loss_evidence: string | null;
  is_qui_tam: boolean;
  earliest_release_date: string | null;
  latest_release_date: string | null;
  final_posture: string | null;
  court_district: string | null;
  usao_district: string | null;
  state_code: string | null;
  fraud_start_year: number | null;
  fraud_end_year: number | null;
  fraud_period_evidence: string | null;
  // Related
  primary_release: PressRelease | null;
  related_releases: RelatedRelease[];
  release_count: number;
}

// Helpers
const fraudTypeLabels: Record<string, string> = {
  healthcare: 'Healthcare',
  defense: 'Defense',
  sba: 'SBA',
  housing: 'Housing',
  customs: 'Customs',
  procurement: 'Procurement',
  grants: 'Grants',
  financial: 'Financial',
  other: 'Other',
};

const formatMoney = (value: string | null): string => {
  if (!value) return '-';
  const num = parseFloat(value);
  if (isNaN(num)) return '-';
  return `$${num.toLocaleString()}`;
};

const formatDate = (dateStr: string | null): string => {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
};

// Decode HTML entities using browser's built-in parser
const decodeHtmlEntities = (text: string): string => {
  if (!text) return '';
  const doc = new DOMParser().parseFromString(text, 'text/html');
  return doc.body.textContent || '';
};

export default function PressReleaseDetailPage() {
  const { pressReleaseId } = useParams<{ pressReleaseId: string }>();
  const navigate = useNavigate();

  const [pressRelease, setPressRelease] = useState<PressReleaseDetailData | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedRelease, setSelectedRelease] = useState<RelatedRelease | null>(null);

  // Load press release detail
  useEffect(() => {
    if (!pressReleaseId) return;
    setLoading(true);
    api
      .get<PressReleaseDetailData>(`/schemes/explore/press-releases/${pressReleaseId}/`)
      .then((res) => {
        setPressRelease(res.data);
      })
      .catch((err) => {
        console.error('Failed to load press release:', err);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [pressReleaseId]);

  // Current release text to display
  const currentReleaseText = selectedRelease?.body_text || pressRelease?.primary_release?.body_text || '';
  const currentReleaseTitle = selectedRelease?.title || pressRelease?.primary_release?.title || '';
  const currentReleaseUrl = selectedRelease?.url || pressRelease?.primary_release?.url || '';
  const currentReleaseDate = selectedRelease?.publish_date || pressRelease?.primary_release?.publish_date;

  if (loading) {
    return (
      <Layout>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
          <CircularProgress />
        </Box>
      </Layout>
    );
  }

  if (!pressRelease) {
    return (
      <Layout>
        <Box sx={{ p: 3 }}>
          <Typography>Press release not found</Typography>
        </Box>
      </Layout>
    );
  }

  return (
    <Layout>
      <Box sx={{ height: 'calc(100vh - 64px)', display: 'flex', flexDirection: 'column' }}>
        {/* Header */}
        <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider', bgcolor: 'background.paper' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <IconButton onClick={() => navigate(-1)}>
              <ArrowBackIcon />
            </IconButton>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="h6" fontWeight={600} sx={{ wordBreak: 'break-word' }}>
                {decodeHtmlEntities(pressRelease.case_name)}
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mt: 0.5 }}>
                <Chip label={fraudTypeLabels[pressRelease.fraud_type] || pressRelease.fraud_type} size="small" color="primary" />
                {pressRelease.is_qui_tam && <Chip label="Qui Tam" size="small" variant="outlined" />}
                {pressRelease.settlement_usd && (
                  <Chip
                    icon={<MoneyIcon />}
                    label={formatMoney(pressRelease.settlement_usd)}
                    size="small"
                    color="success"
                  />
                )}
                {pressRelease.case_number && (
                  <Typography variant="body2" color="text.secondary">
                    Case: {pressRelease.case_number}
                  </Typography>
                )}
              </Box>
            </Box>
          </Box>
        </Box>

        {/* Split Panel */}
        <Box sx={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
          {/* Press Release Text */}
          <Box
            sx={{
              flex: 1,
              overflow: 'auto',
              p: 3,
              bgcolor: '#fafafa',
            }}
          >
            {/* Release selector if multiple */}
            {pressRelease.related_releases.length > 0 && (
              <Paper sx={{ p: 2, mb: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Press Releases ({pressRelease.related_releases.length})
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  <Chip
                    label="Primary"
                    variant={!selectedRelease ? 'filled' : 'outlined'}
                    color={!selectedRelease ? 'primary' : 'default'}
                    onClick={() => setSelectedRelease(null)}
                    size="small"
                  />
                  {pressRelease.related_releases.map((rel) => (
                    <Chip
                      key={rel.id}
                      label={`${rel.release_type.toUpperCase()} - ${formatDate(rel.publish_date)}`}
                      variant={selectedRelease?.id === rel.id ? 'filled' : 'outlined'}
                      color={selectedRelease?.id === rel.id ? 'primary' : 'default'}
                      onClick={() => setSelectedRelease(rel)}
                      size="small"
                    />
                  ))}
                </Box>
              </Paper>
            )}

            {/* Release content */}
            <Paper sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Typography variant="h6">{decodeHtmlEntities(currentReleaseTitle)}</Typography>
                {currentReleaseUrl && (
                  <Tooltip title="Open original press release">
                    <IconButton
                      component={Link}
                      href={currentReleaseUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <OpenInNewIcon />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
              {currentReleaseDate && (
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Published: {formatDate(currentReleaseDate)}
                </Typography>
              )}
              <Divider sx={{ my: 2 }} />
              <Typography
                variant="body2"
                sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.8 }}
              >
                {cleanBodyText(currentReleaseText)}
              </Typography>
            </Paper>
          </Box>
        </Box>
      </Box>
    </Layout>
  );
}
